import { catalog_path, catalog_show_path } from '@/lib/routes';
import { Accordion } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProviderTabs } from '@/components/ProviderTabs';
import type { FolderNode } from '@/components/WorkflowFolder';
import {
  WorkflowFolder,
  buildFolderStructure,
  countWorkflowsInFolder,
  filterWorkflowsInFolder,
  useFolderExpansion,
} from '@/components/WorkflowFolder';
import { useSearchParamQuery } from '@/hooks/useSearchParamQuery';
import * as Icons from 'lucide-react';
import { useMemo } from 'react';
import { AppCard } from './AppCard';
import type { Workflow } from './types';

interface ShowProps {
  workflows: Workflow[];
  providers: any[];
  currentProvider: string;
}

export default function Show({ workflows, providers, currentProvider }: ShowProps) {
  const [searchQuery, setSearchQuery] = useSearchParamQuery();
  const selectedProvider = currentProvider;

  const filterWorkflowsByProvider = (folders: any[], provider: string): any[] => {
    return folders.map((folder) => {
      if (folder.isFolder) {
        return {
          ...folder,
          workflows: folder.workflows.filter((w: Workflow) => w.provider === provider),
        };
      }
      return folder;
    });
  };

  const folderStructure = useMemo(() => {
    return buildFolderStructure(workflows);
  }, [workflows]);

  const filteredFolders = useMemo(() => {
    let filtered = folderStructure;

    if (searchQuery.trim()) {
      filtered = folderStructure
        .map((folder) => filterWorkflowsInFolder(folder, searchQuery))
        .filter(Boolean) as FolderNode[];
    }

    if (selectedProvider) {
      filtered = filterWorkflowsByProvider(filtered, selectedProvider);
    }

    return filtered;
  }, [folderStructure, searchQuery, selectedProvider]);

  const handleProviderChange = (provider: string) => {
    window.location.href = catalog_path(provider);
  };

  const {
    expandedFolders,
    allExpanded,
    handleToggleFolder,
    handleToggleAll,
    handleExpandAll,
    handleCollapseAll,
  } = useFolderExpansion(folderStructure, filteredFolders, searchQuery);

  const totalWorkflows = workflows.length;
  const visibleWorkflows = filteredFolders.reduce(
    (sum, folder) => sum + countWorkflowsInFolder(folder),
    0
  );

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-4xl font-bold text-zinc-900 dark:text-zinc-200">
            AI Workflows Library
          </h1>
          <div>
            <span className="text-zinc-500 dark:text-zinc-200">
              ({visibleWorkflows} of {totalWorkflows} workflows)
            </span>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="flex gap-4 items-center">
          <ProviderTabs
            providers={providers}
            current={currentProvider}
            onChange={handleProviderChange}
          />

          {/* Search Input */}
          <div className="relative max-w-md flex-1">
            <Icons.Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Button variant="outline" size="sm" onClick={handleToggleAll} className="gap-2">
            {allExpanded ? (
              <>
                <Icons.ChevronsUp className="h-4 w-4" />
                Collapse All
              </>
            ) : (
              <>
                <Icons.ChevronsDown className="h-4 w-4" />
                Expand All
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Folder View */}
      <div className="space-y-3">
        {/* Root level workflows (non-folder items) */}
        {filteredFolders.filter((item) => !item.isFolder).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredFolders
              .filter((item) => !item.isFolder)
              .map((item, _index) =>
                item.workflows.map((workflow, wIndex) => (
                  <a
                    key={workflow.id || `${workflow.functionName}-${wIndex}`}
                    href={catalog_show_path(
                      workflow.provider || currentProvider,
                      workflow.functionName
                    )}
                  >
                    <AppCard
                      title={workflow.title}
                      description={workflow.description}
                      Icon={Icons[workflow.icon] as Icons.LucideIcon}
                      colorScheme={workflow.colorScheme}
                      provider={workflow.providerDisplayName}
                    />
                  </a>
                ))
              )}
          </div>
        )}

        {/* Folders */}
        <Accordion type="multiple" value={Array.from(expandedFolders)} className="space-y-2">
          {filteredFolders
            .filter((item) => item.isFolder)
            .map((folder) => (
              <WorkflowFolder
                key={folder.path}
                folder={folder}
                searchQuery={searchQuery}
                expandedFolders={expandedFolders}
                onToggleFolder={handleToggleFolder}
                onExpandAll={handleExpandAll}
                onCollapseAll={handleCollapseAll}
              />
            ))}
        </Accordion>
      </div>

      {filteredFolders.length === 0 && searchQuery.trim() && (
        <div className="text-center py-12">
          <Icons.Search className="mx-auto h-12 w-12 text-zinc-400 mb-4" />
          <p className="text-zinc-500 dark:text-zinc-400 text-lg">
            No workflows found matching &quot;{searchQuery}&quot;
          </p>
          <p className="text-zinc-400 dark:text-zinc-500 text-sm mt-2">
            Try adjusting your search terms or browse all workflows
          </p>
        </div>
      )}
    </div>
  );
}
